import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AppContext = createContext(null);

// ─── Google Sheets Integration ───────────────────────────────────────────────
const GOOGLE_SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL || '';

export const submitToGoogleSheets = async (formData) => {
  const payload = {
    timestamp: new Date().toISOString(),
    name: formData.name,
    email: formData.email,
    service: formData.service || 'Not specified',
    budget: formData.budget || 'Not specified',
    message: formData.message,
    status: 'New',
  };

  if (!GOOGLE_SCRIPT_URL) {
    const existing = JSON.parse(localStorage.getItem(' SitePixel_messages') || '[]');
    existing.unshift({ ...payload, id: Date.now() });
    localStorage.setItem(' SitePixel_messages', JSON.stringify(existing));
    return { success: true, local: true };
  }

  const response = await fetch(GOOGLE_SCRIPT_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const existing = JSON.parse(localStorage.getItem(' SitePixel_messages') || '[]');
  existing.unshift({ ...payload, id: Date.now() });
  localStorage.setItem(' SitePixel_messages', JSON.stringify(existing));

  return { success: true };
};

export const fetchMessages = async () => {
  const stored = JSON.parse(localStorage.getItem(' SitePixel_messages') || '[]');
  return stored;
};

// ─── Projects seed data ───────────────────────────────────────────────────────
const SEED_PROJECTS = [
  {
    id: 1,
    title: 'Velorum E-Commerce Platform',
    description: 'A luxury e-commerce platform with seamless checkout, real-time inventory, and a stunning shopping experience built for a high-end fashion brand.',
    category: 'Web Development',
    technologies: ['React', 'Node.js', 'MongoDB', 'Stripe', 'Tailwind CSS'],
    thumbnail: 'https://images.unsplash.com/photo-1661956602116-aa6865609028?w=800&q=80',
    gallery: ['https://images.unsplash.com/photo-1661956602116-aa6865609028?w=800&q=80', 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=80'],
    liveUrl: '#', githubUrl: '#', featured: true,
    features: ['Real-time inventory management', 'Seamless payment gateway', 'Responsive luxury UI', 'Advanced filtering & search'],
  },
  {
    id: 2,
    title: 'Aura SaaS Dashboard',
    description: 'A comprehensive analytics dashboard for a B2B SaaS platform with real-time data visualization, team collaboration, and AI-powered insights.',
    category: 'Web App Development',
    technologies: ['React', 'D3.js', 'GraphQL', 'PostgreSQL', 'AWS'],
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
    gallery: ['https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'],
    liveUrl: '#', githubUrl: '#', featured: true,
    features: ['Real-time data visualization', 'Team collaboration tools', 'AI-powered insights', 'Role-based access control'],
  },
  {
    id: 3,
    title: 'Luminary Brand Identity',
    description: 'A complete brand identity system for a luxury wellness startup — including logo, typography, color system, packaging, and digital guidelines.',
    category: 'Branding',
    technologies: ['Figma', 'Illustrator', 'After Effects', 'Brand Strategy'],
    thumbnail: 'https://images.unsplash.com/photo-1634942537034-2531766767d1?w=800&q=80',
    gallery: ['https://images.unsplash.com/photo-1634942537034-2531766767d1?w=800&q=80'],
    liveUrl: '#', githubUrl: null, featured: true,
    features: ['Brand strategy', 'Visual identity system', 'Typography & color palette', 'Packaging design'],
  },
  {
    id: 4,
    title: 'Nocturne Mobile App UI',
    description: 'A dark-mode-first mobile UI kit for a music streaming app with immersive visuals, smooth transitions, and an audio-reactive interface.',
    category: 'UI/UX Design',
    technologies: ['Figma', 'Protopie', 'After Effects', 'React Native'],
    thumbnail: 'https://images.unsplash.com/photo-1616803689943-5601631c7fec?w=800&q=80',
    gallery: ['https://images.unsplash.com/photo-1616803689943-5601631c7fec?w=800&q=80'],
    liveUrl: '#', githubUrl: '#', featured: false,
    features: ['Audio-reactive UI', 'Dark mode system', 'Micro-interactions', 'Accessibility-first design'],
  },
  {
    id: 5,
    title: 'Meridian CRM System',
    description: 'A full-featured CRM platform with pipeline management, email automation, analytics, and AI-assisted lead scoring for enterprise sales teams.',
    category: 'Software Development',
    technologies: ['React', 'Python', 'FastAPI', 'PostgreSQL', 'Redis', 'Docker'],
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    gallery: ['https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'],
    liveUrl: '#', githubUrl: '#', featured: false,
    features: ['Pipeline management', 'Email automation', 'AI lead scoring', 'Advanced analytics'],
  },
  {
    id: 6,
    title: 'Solstice Portfolio Website',
    description: 'An award-winning portfolio website for a creative director with scroll-driven animations, WebGL effects, and an immersive storytelling layout.',
    category: 'Web Development',
    technologies: ['Next.js', 'Three.js', 'GSAP', 'WebGL', 'Vercel'],
    thumbnail: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80',
    gallery: ['https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80'],
    liveUrl: '#', githubUrl: '#', featured: false,
    features: ['WebGL animations', 'Scroll-driven storytelling', 'Performance optimized', 'Award-winning design'],
  },
];

const PROJECTS_KEY = ' SitePixel_projects';

const loadProjects = () => {
  try {
    const stored = localStorage.getItem(PROJECTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return SEED_PROJECTS;
};

export const AppProvider = ({ children }) => {
  // Always dark — no toggle
  const darkMode = true;

  const [adminUser, setAdminUser] = useState(null);
  const [projects, setProjects] = useState(loadProjects);

  // Persist projects to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
  }, [projects]);

  // Always keep body in dark mode class
  useEffect(() => {
    document.body.className = '';
  }, []);

  // ── Admin credentials — UPDATE THESE ──────────────────────────
  // Change 'admin' and ' SitePixel2024' to your preferred username & password
  const ADMIN_USERNAME = 'popzzz';
  const ADMIN_PASSWORD = 'popzz@0217';

  const login = useCallback((username, password) => {
    if (username === ADMIN_USERNAME && password === ADMIN_PASSWORD) {
      const user = { username: ADMIN_USERNAME, role: 'admin', token: ' SitePixel-secure-token' };
      setAdminUser(user);
      localStorage.setItem('adminUser', JSON.stringify(user));
      return true;
    }
    return false;
  }, []);

  const logout = useCallback(() => {
    setAdminUser(null);
    localStorage.removeItem('adminUser');
  }, []);

  const addProject = useCallback((project) => {
    setProjects(prev => [...prev, { ...project, id: Date.now() }]);
  }, []);

  const updateProject = useCallback((id, data) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...data } : p));
  }, []);

  const deleteProject = useCallback((id) => {
    setProjects(prev => prev.filter(p => p.id !== id));
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('adminUser');
    if (stored) {
      try { setAdminUser(JSON.parse(stored)); } catch {}
    }
  }, []);

  return (
    <AppContext.Provider value={{
      darkMode,
      adminUser, login, logout,
      projects, addProject, updateProject, deleteProject,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
